import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth, isAdminEmail } from '@craft/shared'
import { logout, submitInquiry, getInquiries, updateInquiryStatus } from '../lib/firebase'
import Logo from '../components/Logo'
import AuthModal from '../components/AuthModal'

// ── ストーリー用コンポーネント ──────────────────────────────

const STORY_STYLES = `
  @keyframes float-a {
    0%, 100% { transform: translateY(0px) rotate(-1deg); }
    50%       { transform: translateY(-6px) rotate(1deg); }
  }
  @keyframes float-b {
    0%, 100% { transform: translateY(-3px) rotate(1deg); }
    50%       { transform: translateY(3px) rotate(-1deg); }
  }
  @keyframes float-c {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    33%       { transform: translateY(-5px) rotate(2deg); }
    66%       { transform: translateY(2px) rotate(-1deg); }
  }
  .float-ken  { animation: float-a 3.2s ease-in-out infinite; }
  .float-tl   { animation: float-b 2.8s ease-in-out infinite; }
  .float-saki { animation: float-c 3.6s ease-in-out infinite; }
`

const CHARS = {
  ken:  { emoji: '🧑‍💻', name: '田中ケン', role: 'あなた',  floatClass: 'float-ken',  color: '#10B981', bg: 'rgba(16,185,129,0.12)',  border: 'rgba(16,185,129,0.35)',  text: '#86efac',  roleColor: '#10B981' },
  tl:   { emoji: '👩‍💼', name: '山田さん', role: '上司',    floatClass: 'float-tl',   color: '#3B8BD4', bg: 'rgba(59,139,212,0.12)', border: 'rgba(59,139,212,0.35)',  text: '#a8c8f0',  roleColor: '#3B8BD4' },
  saki: { emoji: '👨‍💻', name: 'Aさん',   role: '先輩',    floatClass: 'float-saki', color: '#a78bfa', bg: 'rgba(167,139,250,0.12)', border: 'rgba(167,139,250,0.35)', text: '#c4b5fd', roleColor: '#a78bfa' },
}

function Bubble({ char, children, isRight = false, isThought = false }) {
  const c = CHARS[char]
  return (
    <div className={`flex items-end gap-3 ${isRight ? 'flex-row-reverse' : ''}`}>
      {/* アバター */}
      <div className="shrink-0 flex flex-col items-center gap-1.5">
        <div
          className={`w-14 h-14 rounded-full flex items-center justify-center text-2xl ${c.floatClass}`}
          style={{ background: `${c.color}18`, border: `2.5px solid ${c.color}60`, boxShadow: `0 0 12px ${c.color}25` }}
        >
          {c.emoji}
        </div>
        <span
          className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
          style={{ background: `${c.color}18`, color: c.roleColor, border: `1px solid ${c.color}35` }}
        >
          {c.role}
        </span>
        <span className="text-[9px] text-white/25">{c.name}</span>
      </div>
      {/* 吹き出し */}
      <div className="relative max-w-sm">
        {!isRight && !isThought && (
          <div className="absolute -left-2 bottom-4 w-0 h-0"
            style={{ borderTop:'7px solid transparent', borderBottom:'7px solid transparent', borderRight:`9px solid ${c.border}` }} />
        )}
        {isRight && !isThought && (
          <div className="absolute -right-2 bottom-4 w-0 h-0"
            style={{ borderTop:'7px solid transparent', borderBottom:'7px solid transparent', borderLeft:`9px solid ${c.border}` }} />
        )}
        <div
          className={`px-4 py-3 text-sm leading-relaxed ${isRight ? 'rounded-2xl rounded-br-none' : 'rounded-2xl rounded-bl-none'} ${isThought ? 'italic rounded-2xl' : ''}`}
          style={{
            background: isThought ? 'rgba(255,255,255,0.03)' : c.bg,
            border: `1px solid ${isThought ? 'rgba(255,255,255,0.08)' : c.border}`,
            color: isThought ? 'rgba(255,255,255,0.3)' : c.text,
          }}
        >
          {isThought && <span className="text-white/20 mr-1 text-xs">💭</span>}
          {children}
        </div>
      </div>
    </div>
  )
}

function SceneDivider({ label, sub }) {
  return (
    <div className="flex items-center gap-4 my-8">
      <div className="h-px flex-1" style={{ background: 'rgba(255,255,255,0.07)' }} />
      <div className="text-center">
        <div className="text-xs font-bold text-white/60 tracking-wide">{label}</div>
        {sub && <div className="text-[10px] text-white/25 mt-0.5">{sub}</div>}
      </div>
      <div className="h-px flex-1" style={{ background: 'rgba(255,255,255,0.07)' }} />
    </div>
  )
}

function TerminalLine({ children, type = 'cmd' }) {
  const colors = { cmd: '#c8e6c9', error: '#ef9a9a', warn: '#ffcc02', info: '#90caf9' }
  return (
    <div className="font-mono text-xs" style={{ color: colors[type] }}>
      {type === 'cmd' && <span style={{ color: '#81c784' }}>$ </span>}
      {children}
    </div>
  )
}

const FEATURES = [
  {
    icon: '🌿',
    title: 'ブランチ戦略を視覚的に学ぶ',
    desc: 'Git Flow・GitHub Flow・trunk-based development を図解インタラクションで体験。なぜそのブランチ戦略が必要なのかを実感しながら身につけます。',
  },
  {
    icon: '⚡',
    title: 'コンフリクト解消の実践',
    desc: 'マージコンフリクトは怖くない。安全なサンドボックス環境で、実際に起きるコンフリクトを何度でも練習し、解消パターンを習得します。',
  },
  {
    icon: '🔀',
    title: 'PR・レビューワークフロー',
    desc: 'プルリクエストの作り方・レビューの視点・マージ判断まで、チーム開発の流れを一連のシナリオで体験します。',
  },
  {
    icon: '📋',
    title: 'コマンド習得モード',
    desc: '`git rebase -i`、`git bisect`、`git stash` など、現場で使う中・上級コマンドを問題形式で定着。迷わず使えるようになります。',
  },
]

const STEPS = [
  { num: '01', title: 'シナリオを選ぶ', desc: '現場でよくある状況（リリースブランチ作成・hotfix・大規模リベースなど）からシナリオを選択。' },
  { num: '02', title: 'インタラクティブに操作', desc: 'ブランチグラフを見ながらコマンドを入力・選択。操作の結果がリアルタイムで可視化されます。' },
  { num: '03', title: '解説・フィードバック', desc: '操作後に「なぜそうすべきか」を詳しく解説。間違えても怒られません—学びのチャンスです。' },
]

const FAQ = [
  { q: 'Git を全く知らなくても使えますか？', a: '基本的な `git add` / `git commit` を知っていれば始められます。「ブランチとは何か」「add / commit の仕組み」など基礎コースから順番に進められます。' },
  { q: 'ArchiCraft のアカウントで使えますか？', a: 'はい。同じメールアドレス・Googleアカウントでログインできます。' },
  { q: 'どんなエンジニアにおすすめですか？', a: '「git merge と rebase の違いが曖昧」「コンフリクト解消に毎回時間がかかる」「チームのGit運用ルールが理解できていない」と感じているエンジニアに最適です。' },
]

export default function LandingPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [showAuth, setShowAuth] = useState(false)
  const [inquiryForm, setInquiryForm] = useState({ name: '', email: '', subject: '', message: '' })
  const [inquirySubmitting, setInquirySubmitting] = useState(false)
  const [inquiryFeedback, setInquiryFeedback] = useState('')
  const [openFaq, setOpenFaq] = useState(null)

  const isAdminUser = isAdminEmail(user?.email)
  const [inquiries, setInquiries] = useState([])
  const [inquiriesLoading, setInquiriesLoading] = useState(false)

  useEffect(() => {
    if (!isAdminUser) return
    setInquiriesLoading(true)
    getInquiries()
      .then(setInquiries)
      .finally(() => setInquiriesLoading(false))
  }, [isAdminUser])

  const handleUpdateStatus = async (id, status) => {
    await updateInquiryStatus(id, status)
    setInquiries((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)))
  }

  const handleStart = () => {
    if (user) {
      navigate('/dashboard')
    } else {
      setShowAuth(true)
    }
  }

  const handleSubmitInquiry = async (e) => {
    e.preventDefault()
    if (!inquiryForm.email.trim() || !inquiryForm.message.trim()) {
      setInquiryFeedback('メールアドレスとお問い合わせ内容は必須です。')
      return
    }
    try {
      setInquirySubmitting(true)
      setInquiryFeedback('')
      await submitInquiry({ ...inquiryForm, user })
      setInquiryForm((prev) => ({ ...prev, subject: '', message: '' }))
      setInquiryFeedback('お問い合わせを受け付けました。返信をお待ちください。')
    } catch {
      setInquiryFeedback('送信に失敗しました。時間をおいて再試行してください。')
    } finally {
      setInquirySubmitting(false)
    }
  }

  return (
    <div
      className="min-h-screen"
      style={{
        background: 'linear-gradient(180deg, #0a0e1a 0%, #111827 50%, #0a0e1a 100%)',
        fontFamily: "'Noto Sans JP', 'Inter', sans-serif",
      }}
    >
      {/* Nav */}
      <nav className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 md:px-8 py-4 md:py-5">
        <Logo size="md" />
        <div className="flex items-center gap-3 md:gap-6 flex-wrap justify-end">
          <a href="#features" className="hidden md:inline text-sm text-white/60 hover:text-white transition-colors">特徴</a>
          <a href="#how" className="hidden md:inline text-sm text-white/60 hover:text-white transition-colors">学習の流れ</a>
          <a href="#pricing" className="hidden md:inline text-sm text-white/60 hover:text-white transition-colors">料金</a>
          <a href="#faq" className="hidden md:inline text-sm text-white/60 hover:text-white transition-colors">FAQ</a>
          <a href="#contact" className="hidden md:inline text-sm text-white/60 hover:text-white transition-colors">お問い合わせ</a>
          {isAdminUser && (
            <span
              className="text-[10px] px-2 py-1 rounded-full font-semibold"
              style={{ background: 'rgba(16,185,129,0.16)', border: '1px solid rgba(16,185,129,0.45)', color: '#6ee7b7' }}
            >
              管理者ログイン中
            </span>
          )}
          {user ? (
            <div className="flex items-center gap-3">
              <button
                onClick={handleStart}
                className="text-xs md:text-sm px-4 md:px-5 py-2 rounded-lg font-semibold text-white transition-all hover:shadow-lg"
                style={{ background: 'linear-gradient(135deg, #10B981, #059669)' }}
              >
                学習を始める
              </button>
              <button onClick={() => logout()} className="text-xs text-white/40 hover:text-white">
                ログアウト
              </button>
            </div>
          ) : (
            <button
              onClick={handleStart}
              className="text-xs md:text-sm px-4 md:px-5 py-2 rounded-lg font-semibold text-white transition-all hover:shadow-lg hover:shadow-emerald-500/20"
              style={{ background: 'linear-gradient(135deg, #10B981, #059669)' }}
            >
              無料で始める
            </button>
          )}
        </div>
      </nav>

      {/* Hero */}
      <section className="px-4 sm:px-6 md:px-8 pt-16 pb-24 text-center max-w-4xl mx-auto">
        <div
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-8"
          style={{ background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)', color: '#6ee7b7' }}
        >
          🎉 正式リリース — 全コンテンツ無料公開中
        </div>

        <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-white leading-tight mb-6">
          Git を<span style={{ color: '#10B981' }}>「わかる」</span>から
          <br />
          <span style={{ color: '#10B981' }}>「使いこなす」</span>へ。
        </h1>

        <p className="text-lg md:text-xl text-white/60 max-w-2xl mx-auto mb-10 leading-relaxed">
          ブランチ戦略・コンフリクト解消・チーム開発ワークフローを、
          <br className="hidden md:block" />
          インタラクティブなシナリオで実践しながら習得できる学習プラットフォーム。
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={handleStart}
            className="px-8 py-4 rounded-xl font-bold text-white text-base transition-all hover:shadow-xl hover:shadow-emerald-500/25 hover:-translate-y-0.5"
            style={{ background: 'linear-gradient(135deg, #10B981, #059669)' }}
          >
            {user ? '学習を始める →' : '無料で始める →'}
          </button>
          <a
            href="#features"
            className="px-8 py-4 rounded-xl font-semibold text-white/70 text-base transition-all hover:text-white"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)' }}
          >
            詳しく見る →
          </a>
        </div>

        {/* ブランチグラフ イメージ */}
        <div
          className="mt-16 rounded-2xl p-6 md:p-10 text-left"
          style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}
        >
          <div className="text-xs text-white/30 mb-4 font-mono">// git log --graph --oneline</div>
          <div className="font-mono text-sm space-y-1.5">
            <div className="flex items-center gap-3">
              <span className="text-white/20">│</span>
              <span style={{ color: '#10B981' }}>●</span>
              <span className="text-white/70">feat/login</span>
              <span className="text-white/30 text-xs ml-2">← 今ここを学習中</span>
            </div>
            <div className="flex items-center gap-3">
              <span style={{ color: '#10B981' }}>●</span>
              <span className="text-white/30">──</span>
              <span className="text-white/50">develop</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-white/20">│</span>
              <span style={{ color: '#F59E0B' }}>●</span>
              <span className="text-white/70">feat/payment</span>
            </div>
            <div className="flex items-center gap-3">
              <span style={{ color: '#3B82F6' }}>●</span>
              <span className="text-white/30">──</span>
              <span className="text-white/50">main</span>
              <span
                className="text-[10px] px-1.5 py-0.5 rounded font-semibold ml-1"
                style={{ background: 'rgba(59,130,246,0.2)', color: '#93C5FD' }}
              >
                v1.2.0
              </span>
            </div>
          </div>
          <div className="mt-6 pt-5 border-t border-white/5 flex flex-wrap gap-3">
            {['git switch -c feat/login', 'git merge --no-ff', 'git rebase -i HEAD~3'].map((cmd) => (
              <span
                key={cmd}
                className="font-mono text-xs px-3 py-1.5 rounded-lg"
                style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)', color: '#6ee7b7' }}
              >
                {cmd}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Story */}
      <style>{STORY_STYLES}</style>
      <section className="px-4 sm:px-6 md:px-8 py-20 max-w-2xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-black text-white text-center mb-2">
          新人エンジニア・<span style={{ color: '#10B981' }}>田中ケン</span>の話
        </h2>
        <p className="text-white/40 text-center text-sm mb-12">個人開発は得意だったのに、チームに入った途端…</p>

        {/* ── 入社1週目 ── */}
        <SceneDivider label="📅 入社1週目" sub="初めてのチーム開発" />
        <div className="space-y-4">
          <Bubble char="tl">
            田中さん、ログイン機能の実装お願いします！<br />
            <span className="text-xs opacity-60">まずブランチを切って作業を始めてください 🙂</span>
          </Bubble>
          <Bubble char="ken" isRight>
            わかりました！
          </Bubble>
          <Bubble char="ken" isRight isThought>
            …ブランチって何だっけ。mainに直接pushじゃダメなの？
          </Bubble>
        </div>

        {/* ターミナル */}
        <div className="my-5 rounded-xl p-4 space-y-1.5"
          style={{ background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="text-[10px] text-white/20 mb-2 font-mono">terminal</div>
          <TerminalLine>git add .</TerminalLine>
          <TerminalLine>git commit -m "ログイン画面を実装"</TerminalLine>
          <TerminalLine>git push origin main</TerminalLine>
          <TerminalLine type="warn">⚠ mainブランチに直接push...</TerminalLine>
        </div>

        <div className="space-y-4">
          <Bubble char="tl">
            え、mainに直接push！？チームのルールがあって…😅<br />
            <span className="text-xs opacity-60">ブランチを切って作業するのが基本なんです</span>
          </Bubble>
          <Bubble char="ken" isRight>
            す、すみません…！！
          </Bubble>
          <Bubble char="ken" isRight isThought>
            ブランチって何のためにあるんだろう…
          </Bubble>
        </div>

        {/* ── 入社2週目 ── */}
        <SceneDivider label="📅 入社2週目" sub="コンフリクトの壁" />
        <div className="space-y-4">
          <Bubble char="saki">
            styles.css更新してmainにマージしたよ〜
          </Bubble>
          <Bubble char="ken" isRight isThought>
            自分も同じファイル触ってた…とりあえずpullしてみよう
          </Bubble>
        </div>

        <div className="my-5 rounded-xl p-4 space-y-1.5"
          style={{ background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="text-[10px] text-white/20 mb-2 font-mono">terminal</div>
          <TerminalLine>git pull origin main</TerminalLine>
          <TerminalLine type="error">CONFLICT (content): Merge conflict in styles.css</TerminalLine>
          <TerminalLine type="error">Automatic merge failed; fix conflicts and then commit the result.</TerminalLine>
          <TerminalLine type="warn">…何これ！？どうすればいいの！！</TerminalLine>
        </div>

        <div className="space-y-4">
          <Bubble char="ken" isRight>
            パニック…とりあえず<code className="font-mono text-xs bg-black/30 px-1 rounded mx-0.5">git reset --hard</code>すれば消えるかな
          </Bubble>
        </div>

        <div className="my-5 rounded-xl p-4 space-y-1.5"
          style={{ background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="text-[10px] text-white/20 mb-2 font-mono">terminal</div>
          <TerminalLine>git reset --hard HEAD</TerminalLine>
          <TerminalLine type="error">HEAD is now at a1b2c3d ログイン画面を実装</TerminalLine>
          <TerminalLine type="error">⚠ 3日分の作業が消えました</TerminalLine>
        </div>

        <div className="space-y-4">
          <Bubble char="tl">
            あっ…reset --hardしたの？作業全部消えちゃったね😰<br />
            <span className="text-xs opacity-60">コンフリクトの解消方法、知ってた？</span>
          </Bubble>
          <Bubble char="ken" isRight>
            知りませんでした…3日分の作業が…
          </Bubble>
          <Bubble char="ken" isRight isThought>
            個人開発では絶対こんなことなかった。チーム開発って難しい。
          </Bubble>
        </div>

        {/* ── その夜 ── */}
        <SceneDivider label="🌙 その夜" sub="GitCraftと出会う" />
        <div className="space-y-4">
          <Bubble char="ken" isRight isThought>
            「コンフリクト 解消方法」で検索しても、読んでも全然わからない…
          </Bubble>
          <Bubble char="ken" isRight isThought>
            実際の現場みたいなシナリオで練習できる場所ないかな
          </Bubble>
        </div>

        <div className="my-8 rounded-2xl p-6 text-center"
          style={{ background: 'rgba(16,185,129,0.07)', border: '1px solid rgba(16,185,129,0.25)' }}>
          <div className="text-2xl mb-3">✨</div>
          <div className="text-lg font-black text-white mb-1">Git<span style={{ color: '#10B981' }}>Craft</span> と出会う</div>
          <div className="text-white/50 text-sm">チーム開発のシナリオを、体験しながら学ぶ</div>
          <div className="mt-4 font-mono text-xs text-white/25 space-y-1">
            <div>シナリオ1: 初めてのPR</div>
            <div>シナリオ2: コンフリクト発生 ← <span style={{ color: '#10B981' }}>まさにこれだ…！</span></div>
            <div>シナリオ3: レビュー指摘対応</div>
          </div>
        </div>

        <div className="space-y-4">
          <Bubble char="ken" isRight isThought>
            わかった…！HEAD側が自分で、======の下が相手のコードなのか
          </Bubble>
          <Bubble char="ken" isRight isThought>
            マーカーごと消して、残したい1行だけ書けばいいんだ。これなら怖くない
          </Bubble>
        </div>

        {/* ── 1ヶ月後 ── */}
        <SceneDivider label="📅 1ヶ月後" sub="別人のように" />
        <div className="space-y-4">
          <Bubble char="saki">
            また styles.css でコンフリクト出てるね。田中くん、お願いできる？
          </Bubble>
          <Bubble char="ken" isRight>
            任せてください！Aさんに確認して、#0066cc に統一しました💪
          </Bubble>
        </div>

        <div className="my-5 rounded-xl p-4 space-y-1.5"
          style={{ background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="text-[10px] text-white/20 mb-2 font-mono">terminal</div>
          <TerminalLine>git add styles.css</TerminalLine>
          <TerminalLine>git commit -m "fix: コンフリクト解消（#0066ccに統一）"</TerminalLine>
          <TerminalLine type="info">✓ Conflict resolved</TerminalLine>
        </div>

        <div className="space-y-4">
          <Bubble char="tl">
            え、もう解消できたの！？完璧！LGTM 🎉
          </Bubble>
          <Bubble char="saki">
            いつの間にかGitの使い方、めちゃくちゃ上手くなってる！
          </Bubble>
          <Bubble char="ken" isRight isThought>
            あの頃の自分に教えてあげたい。コンフリクトは怖くなかったって。
          </Bubble>
        </div>

        <div className="mt-12 text-center">
          <button
            onClick={handleStart}
            className="px-8 py-4 rounded-xl font-bold text-white text-base transition-all hover:shadow-xl hover:shadow-emerald-500/25 hover:-translate-y-0.5"
            style={{ background: 'linear-gradient(135deg, #10B981, #059669)' }}
          >
            {user ? 'ダッシュボードへ' : 'ケンと同じ経験をなくしたい → 無料で始める'}
          </button>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="px-4 sm:px-6 md:px-8 py-20 max-w-5xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-black text-white text-center mb-4">
          現場で使える Git を、<span style={{ color: '#10B981' }}>体験して</span>覚える
        </h2>
        <p className="text-white/50 text-center mb-14">読むだけでは身につかない。GitCraft はすべてインタラクティブです。</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {FEATURES.map((f) => (
            <div
              key={f.title}
              className="p-6 rounded-2xl"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
            >
              <div className="text-3xl mb-4">{f.icon}</div>
              <h3 className="text-white font-bold text-lg mb-2">{f.title}</h3>
              <p className="text-white/55 text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="px-4 sm:px-6 md:px-8 py-20 max-w-4xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-black text-white text-center mb-14">
          学習の<span style={{ color: '#10B981' }}>流れ</span>
        </h2>
        <div className="space-y-6">
          {STEPS.map((step) => (
            <div
              key={step.num}
              className="flex gap-6 p-6 rounded-2xl items-start"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
            >
              <span
                className="text-2xl font-black shrink-0"
                style={{ color: '#10B981' }}
              >
                {step.num}
              </span>
              <div>
                <h3 className="text-white font-bold text-lg mb-1">{step.title}</h3>
                <p className="text-white/55 text-sm leading-relaxed">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="px-4 sm:px-6 md:px-8 py-20 max-w-3xl mx-auto text-center">
        <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
          料金
        </h2>
        <p className="text-white/50 mb-12">現在は全コンテンツを無料で公開しています。アカウント登録だけで今すぐ始められます。</p>

        <div
          className="p-8 rounded-2xl text-left max-w-sm mx-auto"
          style={{ background: 'rgba(16,185,129,0.07)', border: '1px solid rgba(16,185,129,0.3)' }}
        >
          <div
            className="inline-flex items-center gap-1.5 text-[10px] px-2 py-1 rounded-full font-bold mb-4"
            style={{ background: 'rgba(16,185,129,0.2)', color: '#6ee7b7' }}
          >
            🎉 全コース無料公開中
          </div>
          <p className="text-white/50 text-sm mb-2">完全無料プラン</p>
          <p className="text-4xl font-black text-white mb-1">¥0</p>
          <p className="text-white/40 text-xs mb-6">アカウント登録だけで全コンテンツにアクセス可能</p>
          <ul className="space-y-2 text-sm text-white/60">
            <li>✓ Git基礎コース（4本）</li>
            <li>✓ チーム開発シナリオ（8本）</li>
            <li>✓ 中・上級テクニック（6本）</li>
            <li>✓ インタラクティブシミュレーター（8シナリオ）</li>
          </ul>
        </div>

        <button
          onClick={handleStart}
          className="mt-10 px-8 py-4 rounded-xl font-bold text-white text-base transition-all hover:shadow-xl hover:shadow-emerald-500/25"
          style={{ background: 'linear-gradient(135deg, #10B981, #059669)' }}
        >
          {user ? '学習を始める →' : '無料で始める →'}
        </button>
      </section>

      {/* FAQ */}
      <section id="faq" className="px-4 sm:px-6 md:px-8 py-20 max-w-3xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-black text-white text-center mb-12">FAQ</h2>
        <div className="space-y-3">
          {FAQ.map((item, i) => (
            <div
              key={i}
              className="rounded-xl overflow-hidden"
              style={{ border: '1px solid rgba(255,255,255,0.08)' }}
            >
              <button
                className="w-full flex items-center justify-between px-6 py-4 text-left text-white font-semibold text-sm"
                style={{ background: 'rgba(255,255,255,0.03)' }}
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
              >
                {item.q}
                <span className="text-white/40 ml-4 shrink-0">{openFaq === i ? '▲' : '▼'}</span>
              </button>
              {openFaq === i && (
                <div
                  className="px-6 py-4 text-sm text-white/60 leading-relaxed"
                  style={{ background: 'rgba(255,255,255,0.015)' }}
                >
                  {item.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="px-4 sm:px-6 md:px-8 py-20 max-w-2xl mx-auto">
        <h2 className="text-3xl font-black text-white text-center mb-3">お問い合わせ</h2>
        <p className="text-white/50 text-center text-sm mb-10">ご要望・ご意見もお気軽にどうぞ。</p>

        {inquiryFeedback && (
          <div
            className="mb-6 p-4 rounded-xl text-sm text-center"
            style={{
              background: inquiryFeedback.includes('失敗') ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)',
              border: `1px solid ${inquiryFeedback.includes('失敗') ? 'rgba(239,68,68,0.2)' : 'rgba(16,185,129,0.2)'}`,
              color: inquiryFeedback.includes('失敗') ? '#fca5a5' : '#6ee7b7',
            }}
          >
            {inquiryFeedback}
          </div>
        )}

        <form onSubmit={handleSubmitInquiry} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="お名前（任意）"
              value={inquiryForm.name}
              onChange={(e) => setInquiryForm((p) => ({ ...p, name: e.target.value }))}
              className="px-4 py-3 rounded-xl text-sm text-white placeholder-white/30 outline-none"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
            />
            <input
              type="email"
              placeholder="メールアドレス *"
              value={inquiryForm.email}
              onChange={(e) => setInquiryForm((p) => ({ ...p, email: e.target.value }))}
              required
              className="px-4 py-3 rounded-xl text-sm text-white placeholder-white/30 outline-none"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
            />
          </div>
          <input
            type="text"
            placeholder="件名（任意）"
            value={inquiryForm.subject}
            onChange={(e) => setInquiryForm((p) => ({ ...p, subject: e.target.value }))}
            className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-white/30 outline-none"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
          />
          <textarea
            placeholder="お問い合わせ内容 *"
            value={inquiryForm.message}
            onChange={(e) => setInquiryForm((p) => ({ ...p, message: e.target.value }))}
            required
            rows={5}
            className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-white/30 outline-none resize-none"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
          />
          <button
            type="submit"
            disabled={inquirySubmitting}
            className="w-full py-3 rounded-xl font-bold text-white text-sm transition-all hover:shadow-lg disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #10B981, #059669)' }}
          >
            {inquirySubmitting ? '送信中...' : '送信する'}
          </button>
        </form>
      </section>

      {/* Admin: 問い合わせ一覧 */}
      {isAdminUser && (
        <section className="px-4 sm:px-6 md:px-8 py-16 max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <h2 className="text-2xl font-black text-white">問い合わせ一覧</h2>
            <span className="text-xs px-2 py-1 rounded-full font-semibold"
              style={{ background: 'rgba(16,185,129,0.16)', border: '1px solid rgba(16,185,129,0.35)', color: '#6ee7b7' }}>
              管理者のみ
            </span>
            <span className="text-white/30 text-sm">{inquiries.length}件</span>
          </div>

          {inquiriesLoading ? (
            <p className="text-white/30 text-sm">読み込み中...</p>
          ) : inquiries.length === 0 ? (
            <p className="text-white/30 text-sm">問い合わせはまだありません。</p>
          ) : (
            <div className="space-y-3">
              {inquiries.map((inq) => (
                <div key={inq.id} className="p-5 rounded-xl"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white font-semibold text-sm">{inq.subject}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                        inq.status === 'new'
                          ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                          : 'bg-white/5 text-white/30 border border-white/10'
                      }`}>
                        {inq.status === 'new' ? '未対応' : '対応済み'}
                      </span>
                    </div>
                    <button
                      onClick={() => handleUpdateStatus(inq.id, inq.status === 'new' ? 'done' : 'new')}
                      className="text-xs text-white/40 hover:text-white transition-colors shrink-0"
                    >
                      {inq.status === 'new' ? '対応済みにする' : '未対応に戻す'}
                    </button>
                  </div>
                  <p className="text-white/50 text-xs mb-1">{inq.name || '（名前なし）'} — {inq.email}</p>
                  <p className="text-white/70 text-sm whitespace-pre-wrap">{inq.message}</p>
                  {inq.createdAt?.toDate && (
                    <p className="text-white/20 text-xs mt-2">
                      {inq.createdAt.toDate().toLocaleString('ja-JP')}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Footer */}
      <footer className="border-t border-white/5 px-4 sm:px-8 py-8">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <Logo size="sm" />
          <div className="flex gap-6 text-xs text-white/30">
            <a href="/terms" className="hover:text-white/60">利用規約</a>
            <a href="/privacy" className="hover:text-white/60">プライバシーポリシー</a>
            <a href="/tokushoho" className="hover:text-white/60">特定商取引法</a>
          </div>
          <p className="text-xs text-white/20">© 2026 Craft Series</p>
        </div>
      </footer>

      {showAuth && (
        <AuthModal
          onClose={() => setShowAuth(false)}
          onSuccess={() => setShowAuth(false)}
        />
      )}
    </div>
  )
}
